import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ZIndex } from '@/consts/z-indexes.consts';

interface TourCircleHighlightProps {
  anchorEl: HTMLElement;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 6;

const measure = (el: HTMLElement): Rect => {
  const r = el.getBoundingClientRect();
  const size = Math.max(r.width, r.height) + PADDING * 2;
  return {
    top: r.top + r.height / 2 - size / 2,
    left: r.left + r.width / 2 - size / 2,
    width: size,
    height: size,
  };
};

const isSameRect = (a: Rect, b: Rect) =>
  a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;

export const TourCircleHighlight = ({ anchorEl }: TourCircleHighlightProps) => {
  const [rect, setRect] = useState<Rect>(() => measure(anchorEl));

  // Canvas anchors move by CSS transform when the viewport pans or zooms, which fires no scroll or
  // resize event, so the rect is re-read every frame rather than driven by listeners.
  useEffect(() => {
    let frame = requestAnimationFrame(function tick() {
      const next = measure(anchorEl);
      setRect((prev) => (isSameRect(prev, next) ? prev : next));
      frame = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(frame);
  }, [anchorEl]);

  return createPortal(
    <div
      className="tour-circle-highlight"
      style={{
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        zIndex: ZIndex.Overlays.PRODUCT_TOUR_OVERLAY,
      }}
    />,
    document.body,
  );
};
